/** The form state of a job description, and the payload the API expects. */

import { sectorsInfoList } from 'helper/helper';

const SECTORS_INFO = sectorsInfoList();

// A template holds the readable sector name, the same one a mission gets from
// its qualification type.
export const activityFromQualificationType = (type) =>
  SECTORS_INFO[type]?.name || type || '';

export const TOOL_PROVIDERS = ['company', 'temp_worker'];
export const DEFAULT_TOOL_PROVIDER = 'temp_worker';

export const TOOL_PROVIDER_LABELS = {
  company: 'Entreprise',
  temp_worker: 'Intérimaire',
};

export const EMPTY_TEMPLATE_FORM = {
  name: '',
  activity: '',
  // Only filters the qualification list; the template stores `activity`.
  sector: '',
  qualification: '',
  qualificationLabel: '',
  tools: [],
  licences: [],
  languages: [],
  contact_name: '',
  contact_poste: 'Responsable',
  location: '',
  salary: '',
  isIndexedToMinimumWage: false,
  other: '',
  other_interim: '',
  position_at_risk: false,
  penibilite: {},
};

// Some names of the tools referential start with a space.
const toolNameKey = (name) =>
  typeof name === 'string' ? name.trim().toLowerCase() : '';

export const normalizeToolProvider = (provider) =>
  TOOL_PROVIDERS.includes(provider) ? provider : DEFAULT_TOOL_PROVIDER;

/** Legacy templates still hold plain strings instead of {name, providedBy}. */
export const normalizeTemplateTools = (input) => {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  return input.reduce((tools, item) => {
    const rawName = typeof item === 'string' ? item : item?.name;
    const key = toolNameKey(rawName);
    if (!key || seen.has(key)) return tools;
    seen.add(key);
    tools.push({
      name: rawName.trim(),
      providedBy: normalizeToolProvider(item?.providedBy),
    });
    return tools;
  }, []);
};

// The API sends the ten BeSTT keys on read and reads a missing key as false,
// so the form only keeps the factors that are on.
export const normalizePenibilite = (penibilite) => {
  if (!penibilite || typeof penibilite !== 'object') return {};
  return Object.keys(penibilite).reduce((selection, besttField) => {
    if (penibilite[besttField] === true) selection[besttField] = true;
    return selection;
  }, {});
};

export const hasSelectedPenibilite = (penibilite) =>
  Object.keys(normalizePenibilite(penibilite)).length > 0;

/** Auto prefix of the job description name, as the company front writes it. */
export const templateNamePrefix = (qualificationLabel) =>
  qualificationLabel?.trim() ? `${qualificationLabel.trim()} - ` : '';

/** The typed part of the name, behind the prefix the qualification imposes. */
export const getTemplateNameSuffix = (name, qualificationLabel) => {
  const prefix = templateNamePrefix(qualificationLabel);
  const full = String(name ?? '');
  return prefix && full.startsWith(prefix) ? full.slice(prefix.length) : full;
};

// Only the part the admin typed counts: a name left at the bare prefix is empty.
export const isTemplateNameFilled = (form) => {
  const prefix = templateNamePrefix(form?.qualificationLabel);
  const name = String(form?.name ?? '');
  if (!name.trim()) return false;
  return name.startsWith(prefix) ? name.slice(prefix.length).trim() !== '' : true;
};

/**
 * Comparison key of a job description name: case and spacing are ignored, so
 * « Boucher H/F - Lyon » and « boucher h/f -  lyon » are the same name.
 * Kept aligned with normalizeTemplateName on the api side.
 */
export const normalizeTemplateName = (name) =>
  String(name ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

/** True when another active job description of the company already has that name. */
export const isTemplateNameTaken = (name, templates, currentId) => {
  const wanted = normalizeTemplateName(name);
  if (!wanted) return false;
  return (templates || []).some(
    (template) =>
      !template?.archived &&
      String(template?._id) !== String(currentId) &&
      normalizeTemplateName(template?.name) === wanted
  );
};

/**
 * Name proposed for a copy: "… (copie)", then "(copie 2)", "(copie 3)"… up to the
 * first free one, so the Dupliquer button works straight away. The qualification
 * prefix stays untouched: the suffix goes at the end.
 */
export const buildDuplicateTemplateName = (name, templates) => {
  const base = String(name ?? '').trim();
  if (!base) return '';
  const candidate = (rank) =>
    rank === 1 ? `${base} (copie)` : `${base} (copie ${rank})`;
  let rank = 1;
  while (isTemplateNameTaken(candidate(rank), templates)) {
    rank += 1;
  }
  return candidate(rank);
};

export const templateToFormData = (template) => ({
  ...EMPTY_TEMPLATE_FORM,
  name: template?.name || '',
  activity: template?.activity || '',
  // The id is resolved from the label once the referential is loaded.
  qualification: '',
  qualificationLabel: template?.qualification || template?.title || '',
  tools: normalizeTemplateTools(template?.tools),
  licences: template?.licences || [],
  languages: template?.language || [],
  contact_name: template?.contact?.name || '',
  contact_poste: template?.contact?.poste || '',
  location: template?.location || '',
  salary:
    typeof template?.salary?.wage_per_hour === 'number'
      ? String(template.salary.wage_per_hour)
      : '',
  isIndexedToMinimumWage: !!template?.isIndexedToMinimumWage,
  position_at_risk: !!template?.conditions?.position_at_risk,
  penibilite: normalizePenibilite(template?.conditions?.penibilite),
});

// The wizard keeps the selection while the switch is off so it survives a
// change of mind; the API must only receive it when the flag is on.
export const buildTemplatePayload = (form) => ({
  name: form.name?.trim(),
  title: form.qualificationLabel,
  activity: form.activity,
  qualification: form.qualificationLabel,
  tools: normalizeTemplateTools(form.tools),
  licences: form.licences,
  language: form.languages,
  contact: { name: form.contact_name, poste: form.contact_poste },
  other: form.other,
  other_interim: form.other_interim,
  location: form.location,
  salary: { wage_per_hour: form.salary },
  isIndexedToMinimumWage: !!form.isIndexedToMinimumWage,
  conditions: {
    position_at_risk: !!form.position_at_risk,
    penibilite: form.position_at_risk ? normalizePenibilite(form.penibilite) : {},
  },
});

// The same steps as the company wizard, in the same order.
export const TEMPLATE_STEPS = [
  { key: 'activity', label: "Secteur d'activité" },
  { key: 'risk', label: 'Poste à risque' },
  { key: 'prerequis', label: 'Pré-requis' },
  { key: 'locationSalary', label: 'Lieu & rémunération' },
  { key: 'details', label: 'Détail' },
  { key: 'confirmation', label: 'Confirmation' },
];

export const TEMPLATE_STEP_KEYS = TEMPLATE_STEPS.map(({ key }) => key);

// The api refuses a duplicate with "Template name already used": both sides
// say the same thing to the admin.
export const TEMPLATE_NAME_TAKEN_MESSAGE =
  'Une fiche de poste porte déjà ce nom';
export const TEMPLATE_NAME_TAKEN_API_MESSAGE = 'Template name already used';

const hasAddress = (location) =>
  !!location &&
  (typeof location !== 'object' || Object.keys(location).length > 0);

/** What keeps a step from being left, if anything. */
export const findTemplateStepError = (stepKey, form, siblings, currentId) => {
  switch (stepKey) {
    case 'activity':
      if (!form.activity || !form.qualificationLabel)
        return 'Veuillez sélectionner un secteur et une qualification';
      if (!isTemplateNameFilled(form))
        return "Veuillez indiquer l'intitulé de la fiche de poste";
      if (isTemplateNameTaken(form.name, siblings, currentId))
        return TEMPLATE_NAME_TAKEN_MESSAGE;
      return null;
    case 'risk':
      if (form.position_at_risk && !hasSelectedPenibilite(form.penibilite))
        return 'Veuillez sélectionner au moins un facteur de pénibilité';
      return null;
    case 'locationSalary':
      if (!hasAddress(form.location)) return 'Veuillez renseigner une adresse';
      if (!form.salary) return 'Veuillez renseigner un taux horaire';
      return null;
    case 'details':
      if (!form.contact_name || !form.contact_poste)
        return 'Veuillez compléter les informations du responsable';
      return null;
    default:
      return null;
  }
};

/** The first step that is still incomplete, checked before saving. */
export const findTemplateFormError = (form, siblings, currentId) => {
  for (const { key } of TEMPLATE_STEPS) {
    const message = findTemplateStepError(key, form, siblings, currentId);
    if (message) return { stepKey: key, message };
  }
  return null;
};
